/*
* Ferguson Finch wishlist.
*
* Saved products are kept in this browser (localStorage). For logged-in
* customers every change is also sent to the Ferguson Finch Wishlist app at
* /apps/wishlist/items, which saves the list to their customer account so it
* appears on every device. When a guest logs in, their browser list is merged
* into their account.
*
* Markup hooks:
*   [data-wishlist-toggle][data-product-id][data-product-handle]  save/unsave button
*   [data-wishlist-count]   number of saved items
*   [data-wishlist-page]    wishlist page (sections/wishlist.liquid)
*
* Settings and the customer's saved list come from snippets/wishlist-config.liquid.
*/

(function() {
  var config = window.FFWishlist;
  if (!config) return;

  var STORAGE_KEY = 'ff-wishlist';
  var NOTE_KEY = 'ff-wishlist-note-shown';
  // After a save, the page can briefly show the old list from the server, so trust this browser for a short while.
  var TRUST_LOCAL_MS = 2 * 60 * 1000;

  var state = { owner: null, items: [], syncedAt: 0 };
  var queue = Promise.resolve();

  // ---------------------------------------------------------------------------
  // Storage

  function load() {
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved && Array.isArray(saved.items)) return saved;
    } catch (e) {}
    return { owner: null, items: [], syncedAt: 0 };
  }

  function save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  function has(id) {
    return state.items.some(function(item) { return item.id === String(id); });
  }

  function handleFor(id, fallback) {
    var found = state.items.filter(function(item) { return item.id === String(id); })[0];
    return found ? found.handle : fallback;
  }

  // Keep the server's order, but reuse handles we already know.
  function fromServerIds(ids, knownItems) {
    var known = {};
    knownItems.concat(state.items).forEach(function(item) { known[item.id] = item.handle; });
    return ids
      .map(function(id) { return { id: String(id), handle: known[String(id)] }; })
      .filter(function(item) { return item.handle; });
  }

  // ---------------------------------------------------------------------------
  // Server (logged-in customers only)

  // Saves run one at a time. Each reply holds the whole list as it was after that save,
  // so only the reply to the last save in flight is applied. Earlier replies would put
  // back hearts the customer has since changed (the "fill, unfill, fill" flicker).
  var inFlight = 0;

  function send(action, ids) {
    inFlight++;
    var request = function() {
      return fetch(config.proxyUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ action: action, ids: ids })
      }).then(function(res) {
        if (!res.ok) throw new Error('Wishlist save failed: ' + res.status);
        return res.json();
      });
    };
    var result = queue.then(request, request);
    queue = result.catch(function() {});
    return result.then(
      function(data) { inFlight--; return data; },
      function(error) { inFlight--; throw error; }
    );
  }

  function applyServerList(ids, knownItems) {
    if (inFlight > 0) return;
    state.items = fromServerIds(ids, knownItems);
    state.syncedAt = Date.now();
    save();
    paint();
  }

  function reconcile() {
    var local = load();
    var server = config.serverItems || [];

    if (!config.customerId) {
      // Logged out: forget any list that belonged to an account on this browser.
      state = local.owner ? { owner: null, items: [], syncedAt: 0 } : local;
      save();
      return;
    }

    if (local.owner === config.customerId && Date.now() - local.syncedAt < TRUST_LOCAL_MS) {
      state = local;
      return;
    }

    state = { owner: config.customerId, items: server.slice(), syncedAt: Date.now() };

    // Guest list from before logging in: merge it into the account.
    if (!local.owner && local.items.length) {
      var extra = local.items.filter(function(item) { return !has(item.id); });
      state.items = extra.concat(state.items);
      save();
      if (extra.length) {
        var knownItems = local.items.concat(server);
        send('merge', extra.map(function(item) { return item.id; }))
          .then(function(data) { applyServerList(data.items, knownItems); })
          .catch(function(error) { console.warn(error); });
      }
      return;
    }

    save();
  }

  // ---------------------------------------------------------------------------
  // Changes

  function toggle(id, handle) {
    id = String(id);
    var adding = !has(id);
    var item = { id: id, handle: handle };

    if (adding) {
      state.items.unshift(item);
    } else {
      state.items = state.items.filter(function(item) { return item.id !== id; });
    }
    state.syncedAt = Date.now();
    save();
    paint();
    notify(adding);
    document.dispatchEvent(new CustomEvent('ff:wishlist:change', { detail: { id: id, saved: adding, items: state.items } }));

    if (!config.customerId) return;

    send(adding ? 'add' : 'remove', [id])
      .then(function(data) { applyServerList(data.items, [item]); })
      .catch(function(error) {
        console.warn(error);
        // Undo just this change, leaving any other taps alone.
        if (adding) {
          state.items = state.items.filter(function(saved) { return saved.id !== id; });
        } else if (!has(id)) {
          state.items.unshift(item);
        }
        save();
        paint();
        toast("Sorry, we couldn't update your wishlist. Please try again.");
      });
  }

  // ---------------------------------------------------------------------------
  // Display

  function paint() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function(button) {
      var saved = has(button.dataset.productId);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.classList.toggle('is-saved', saved);
      var text = button.querySelector('[data-wishlist-text]');
      // Only touch text that changed, so the MutationObserver below doesn't loop.
      setText(text, saved ? config.text.added : config.text.add);
    });

    document.querySelectorAll('[data-wishlist-count]').forEach(function(count) {
      setText(count, String(state.items.length));
      count.hidden = state.items.length === 0;
    });

    document.querySelectorAll('[data-wishlist-link]').forEach(function(link) {
      link.classList.toggle('has-items', state.items.length > 0);
    });

    var page = document.querySelector('[data-wishlist-page]');
    if (page) updateEmptyMessage(page);
  }

  // The "your wishlist is empty" message follows the cards actually on the page.
  function updateEmptyMessage(page) {
    var grid = page.querySelector('[data-wishlist-grid]');
    var empty = page.querySelector('[data-wishlist-empty]');
    var loading = page.querySelector('[data-wishlist-loading]');
    if (!grid || !empty || (loading && !loading.hidden)) return;
    var showEmpty = grid.children.length === 0;
    if (empty.hidden === showEmpty) empty.hidden = !showEmpty;
  }

  function setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  var toastEl;
  var toastTimer;

  function toast(message, html) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '';
    var text = document.createElement('span');
    text.textContent = message;
    toastEl.appendChild(text);
    if (html) toastEl.appendChild(html);
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function() { toastEl.classList.remove('is-visible'); }, html ? 6000 : 2500);
  }

  function notify(adding) {
    var showNote = adding && !config.customerId && config.text.guestNote && !sessionStorageGet(NOTE_KEY);
    if (showNote) {
      sessionStorageSet(NOTE_KEY, '1');
      var link = document.createElement('a');
      link.href = config.loginUrl;
      link.textContent = config.text.logIn;
      var note = document.createElement('span');
      note.className = 'wishlist-toast__note';
      note.appendChild(document.createTextNode(config.text.guestNote + ' '));
      note.appendChild(link);
      toast(config.text.addedMessage || '', note);
      return;
    }
    if (!config.showToast) return;
    toast(adding ? config.text.addedMessage : config.text.removedMessage);
  }

  function sessionStorageGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function sessionStorageSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Wishlist page

  function renderPage(page) {
    var grid = page.querySelector('[data-wishlist-grid]');
    var empty = page.querySelector('[data-wishlist-empty]');
    var loading = page.querySelector('[data-wishlist-loading]');
    if (!grid) return;

    var items = state.items.slice();
    if (loading) loading.hidden = true;
    if (!items.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    if (loading) loading.hidden = false;

    var root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    Promise.all(items.map(function(item) {
      return fetch(root + 'products/' + encodeURIComponent(item.handle) + '?section_id=api-wishlist-grid-item')
        .then(function(res) { return res.ok ? res.text() : ''; })
        .catch(function() { return ''; });
    })).then(function(results) {
      grid.innerHTML = '';
      results.forEach(function(html) {
        if (!html || html.indexOf('data-unpublished') !== -1) return;
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var content = holder.querySelector('[data-api-content]');
        if (!content) return;
        while (content.firstElementChild) grid.appendChild(content.firstElementChild);
      });
      // Cards added after load miss the theme's scroll fade-in, so show them in their finished state.
      grid.querySelectorAll('[data-aos]').forEach(function(el) { el.classList.add('aos-animate'); });
      if (loading) loading.hidden = true;
      if (empty) empty.hidden = grid.children.length > 0;
      paint();
    });
  }

  function onPageChange(event) {
    var page = document.querySelector('[data-wishlist-page]');
    if (!page || event.detail.saved) return;
    var grid = page.querySelector('[data-wishlist-grid]');
    var button = grid && grid.querySelector('[data-wishlist-toggle][data-product-id="' + event.detail.id + '"]');
    var card = button && button.closest('[data-product-block]');
    if (card) card.remove();
    updateEmptyMessage(page);
  }

  // ---------------------------------------------------------------------------
  // Start

  function init() {
    reconcile();
    paint();

    document.addEventListener('click', function(event) {
      var button = event.target.closest('[data-wishlist-toggle]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      toggle(button.dataset.productId, button.dataset.productHandle);
    }, true);

    document.addEventListener('ff:wishlist:change', onPageChange);

    // Product cards added later (filters, quick view, recently viewed) need their state too.
    var pending = false;
    new MutationObserver(function() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function() {
        pending = false;
        paint();
      });
    }).observe(document.body, { childList: true, subtree: true });

    // Another tab changed the list.
    window.addEventListener('storage', function(event) {
      if (event.key !== STORAGE_KEY) return;
      state = load();
      paint();
    });

    var page = document.querySelector('[data-wishlist-page]');
    if (page) renderPage(page);
  }

  window.FFWishlist.has = has;
  window.FFWishlist.items = function() { return state.items.slice(); };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
